import { Button, cn } from '@heroui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';

import { usePlansModalVisibility } from '../../hooks/usePlansModalVisibility';
import { useSession } from '../../hooks/useSession';
import { getMarketingCtaLocation, pushSignupCtaClick } from '../../lib/gtmDataLayer';

const DEMO_HREF = 'https://calendar.app.google/NXbAeTAUwaBGh5x49';

function WorkspaceButton({
  variant,
  ctaLocation,
  onExploreClick,
}: {
  variant: 'solid' | 'bordered';
  ctaLocation: string;
  onExploreClick?: () => void;
}) {
  const { isSignedIn, user } = useSession();
  const isSolid = variant === 'solid';

  if (isSignedIn) {
    const isActive = user?.subscription.isActive;
    const isExploreMode = !isActive && !user?.subscription.hasUsedTrial;

    return (
      <Button
        as={NextLink}
        href="/"
        size="lg"
        variant={isSolid ? undefined : 'bordered'}
        className={isSolid ? 'bg-foreground text-content1' : undefined}
      >
        {isActive ? 'Open in Kreatli' : isExploreMode ? 'Continue in workspace' : 'Upgrade to restore access'}
      </Button>
    );
  }

  return (
    <Button
      as={NextLink}
      href="/sign-up"
      size="lg"
      variant={isSolid ? undefined : 'bordered'}
      className={isSolid ? 'bg-foreground text-content1' : undefined}
      onPress={() => {
        pushSignupCtaClick({ location: ctaLocation });
        onExploreClick?.();
      }}
    >
      Explore free, no card required
    </Button>
  );
}

export function HeroCtaButtons({
  className,
  leading,
  trailing,
  onExploreClick,
  ctaLocation,
  showTrialCta = true,
}: {
  className?: string;
  /** Optional first button (e.g. link to a free tool). Auth CTA renders as bordered when set. */
  leading?: ReactNode;
  /** Optional second button after auth CTA (e.g. link to a related free tool). */
  trailing?: ReactNode;
  onExploreClick?: () => void;
  /** Amplitude/GTM location. Defaults from the current path. */
  ctaLocation?: string;
  /**
   * On commercial platform pages, signed-in explore/expired users get a plans-modal path
   * without changing the logged-out “Explore free” offer.
   */
  showTrialCta?: boolean;
}) {
  const router = useRouter();
  const { isSignedIn, user } = useSession();
  const setIsPlansModalVisible = usePlansModalVisibility((state) => state.setIsVisible);
  const resolvedLocation = ctaLocation ?? getMarketingCtaLocation(router.asPath);
  const isExpired = isSignedIn && !user?.subscription.isActive && !!user?.subscription.hasUsedTrial;
  const isExploreMode = isSignedIn && !user?.subscription.isActive && !user?.subscription.hasUsedTrial;
  const authVariant = leading ? 'bordered' : 'solid';
  const showPlansPath = showTrialCta && isSignedIn && !user?.subscription.isActive;

  return (
    <div className={cn('mt-4 flex flex-col items-center justify-center gap-4 sm:flex-row', className)}>
      {leading}
      <WorkspaceButton variant={authVariant} ctaLocation={resolvedLocation} onExploreClick={onExploreClick} />
      {showPlansPath ? (
        <Button
          size="lg"
          variant="bordered"
          onPress={() => setIsPlansModalVisible(true, resolvedLocation)}
        >
          {isExploreMode ? 'Start a free trial' : 'Choose a plan'}
        </Button>
      ) : null}
      {trailing}
      {!leading && !trailing && !isSignedIn && !showPlansPath && (
        <Button
          as="a"
          href={DEMO_HREF}
          target="_blank"
          rel="noopener noreferrer nofollow"
          size="lg"
          variant="bordered"
        >
          Book a Demo
        </Button>
      )}
      {!leading && !trailing && isExpired && !showPlansPath && (
        <Button
          as="a"
          href={DEMO_HREF}
          target="_blank"
          rel="noopener noreferrer nofollow"
          size="lg"
          variant="bordered"
        >
          Book a Demo
        </Button>
      )}
    </div>
  );
}
